import { Controller } from "@hotwired/stimulus"
import { Spring, VelocityTracker, project, rubberband, prefersReducedMotion } from "../lib/spring"

// The public navigation drawer, below the header's breakpoint.
//
// It is a sheet lying on top of the page, not a block pushed into it: the
// article underneath keeps its place, so closing the menu puts a reader back
// on the line they were on.
//
// Its position is one number, and everything else is read off that number —
// how far the sheet has travelled, how dark the page behind it is, how far the
// three bars of the menu button have folded into a cross. A finger moves that
// number directly while one is on the sheet; a spring moves it the rest of the
// time. Nothing here uses a CSS transition, because a transition runs from
// where it was told to start to where it was told to end and cannot be caught
// half way and thrown back.
//
// Three ways in and out: the menu button, a swipe from the right edge of the
// screen, and a swipe on the sheet or on the dimmed page behind it. Escape
// closes it and puts focus back on the button it came from (WCAG 2.1.2), and
// Tab is held inside it while it is open — otherwise a keyboard user tabs into
// a page that is behind a scrim and cannot be seen.
//
// Above the breakpoint there is no drawer at all: the nav is a row of pills
// beside the wordmark, and everything here is torn down.
export default class extends Controller {
  static targets = ["menu", "toggle", "scrim"]

  // How far from the right edge a swipe may start if it is to open the drawer.
  static EDGE = 24
  // Movement before we decide a drag is sideways and not a scroll. Below this
  // both readings are still live and neither has won.
  static INTENT = 10

  connect() {
    // The breakpoint is in three places and they have to agree: this file, the
    // .nav-drawer media query in application.tailwind.css, and the xl: classes
    // in _header.html.erb.
    this.desktop = window.matchMedia("(min-width: 1280px)")
    this.tracker = new VelocityTracker()
    this.drag = null
    this.animation = null
    this.x = 0
    this.destination = 0
    // Not zero: zero is a real reading of performance.now() a few hundred
    // milliseconds after the page loads, and a tap in that window would be
    // mistaken for the click that follows a drag.
    this.draggedAt = -Infinity

    this.onPointerDown = this.pointerDown.bind(this)
    this.onPointerMove = this.pointerMove.bind(this)
    this.onPointerUp = this.pointerUp.bind(this)
    this.onKeydown = this.keydown.bind(this)
    this.onClick = this.clickGuard.bind(this)
    this.onLayout = this.layout.bind(this)

    // On document rather than on the header: a drag can leave the sheet, and
    // Escape has to work from wherever the focus ended up.
    document.addEventListener("pointerdown", this.onPointerDown)
    document.addEventListener("pointermove", this.onPointerMove, { passive: false })
    document.addEventListener("pointerup", this.onPointerUp)
    document.addEventListener("pointercancel", this.onPointerUp)
    document.addEventListener("keydown", this.onKeydown)
    document.addEventListener("click", this.onClick, { capture: true })
    this.desktop.addEventListener("change", this.onLayout)

    this.layout()
  }

  disconnect() {
    document.removeEventListener("pointerdown", this.onPointerDown)
    document.removeEventListener("pointermove", this.onPointerMove)
    document.removeEventListener("pointerup", this.onPointerUp)
    document.removeEventListener("pointercancel", this.onPointerUp)
    document.removeEventListener("keydown", this.onKeydown)
    document.removeEventListener("click", this.onClick, { capture: true })
    this.desktop.removeEventListener("change", this.onLayout)
    this.animation?.stop()
    this.unlockScroll()
  }

  // ---- Actions ---------------------------------------------------------

  toggle() {
    if (this.recentlyDragged) return
    this.opened ? this.close({ focus: true }) : this.open({ focus: true })
  }

  // The dimmed page behind the sheet. A swipe that ended there has already
  // closed it, and the click that follows the release must not reopen it.
  dismiss() {
    if (this.recentlyDragged) return
    this.close()
  }

  // A drag ends with a click wherever the finger left the sheet, and with a
  // mouse the browser sends it even though the move was cancelled. Somebody
  // who pushed the drawer shut across the middle of the list meant to shut the
  // drawer, not to follow the link they happened to let go over. Caught on the
  // way down, before the link or Turbo sees it.
  clickGuard(event) {
    if (!this.recentlyDragged) return
    if (!(this.menuTarget.contains(event.target) || this.scrimTarget.contains(event.target))) return

    event.preventDefault()
    event.stopPropagation()
  }

  open({ velocity = 0, focus = false } = {}) {
    this.destination = 0

    // Measured now: the header is a different height at different widths, and
    // the sheet hangs from underneath it.
    this.element.style.setProperty("--header-h", `${this.element.offsetHeight}px`)

    this.reveal()
    this.setExpanded(true)
    this.lockScroll()
    this.animateTo(0, velocity)

    if (focus) this.focusables[1]?.focus()
  }

  close({ velocity = 0, focus = false } = {}) {
    this.destination = this.closedX
    this.setExpanded(false)
    this.unlockScroll()

    // The sheet is about to be taken out of the layout. Anything focused
    // inside it would take the focus ring out with it.
    const inside = this.menuTarget.contains(document.activeElement)
    if (focus || inside) this.toggleTarget.focus()

    this.animateTo(this.closedX, velocity)
  }

  // ---- Keyboard --------------------------------------------------------

  keydown(event) {
    if (this.desktop.matches || !this.opened) return

    if (event.key === "Escape") {
      event.preventDefault()
      return this.close({ focus: true })
    }

    if (event.key !== "Tab") return

    const stops = this.focusables
    if (stops.length === 0) return

    const first = stops[0]
    const last = stops[stops.length - 1]

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  // ---- Gesture ---------------------------------------------------------

  pointerDown(event) {
    if (this.desktop.matches) return
    if (event.pointerType === "mouse" && event.button !== 0) return

    // Things that do their own dragging: the slider on the front page, a map,
    // a text field. A swipe that starts inside one of them belongs to it, and
    // an edge gesture must not take it away.
    if (event.target.closest?.(".splide, .leaflet-container, input, textarea, select")) return

    const onSheet = this.menuTarget.contains(event.target)
    const onScrim = this.scrimTarget.contains(event.target)
    const fromEdge = !this.opened &&
      event.clientX >= document.documentElement.clientWidth - this.constructor.EDGE

    if (!(fromEdge || (this.opened && (onSheet || onScrim)))) return

    this.drag = {
      id: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      from: this.x,
      committed: false
    }
  }

  pointerMove(event) {
    const drag = this.drag
    if (!drag || event.pointerId !== drag.id) return

    const dx = event.clientX - drag.x
    const dy = event.clientY - drag.y

    if (!drag.committed) {
      // Both readings — sideways and scroll — stay live until one of them is
      // far enough ahead to be the answer.
      if (Math.abs(dx) < this.constructor.INTENT && Math.abs(dy) < this.constructor.INTENT) return
      if (Math.abs(dx) <= Math.abs(dy)) return (this.drag = null)

      drag.committed = true

      // Catching the sheet in flight starts from where it is on the screen,
      // not from where it was heading.
      this.animation?.stop()
      this.animation = null
      drag.from = this.x
      drag.x = event.clientX
      this.reveal()
      this.menuTarget.classList.add("is-dragging")
      this.menuTarget.style.willChange = "transform"
      this.menuTarget.setPointerCapture?.(drag.id)
      this.tracker.reset()
      this.tracker.push(this.x, event.timeStamp)
      return
    }

    if (event.cancelable) event.preventDefault()

    let x = drag.from + (event.clientX - drag.x)
    if (x < 0) x = rubberband(x, this.width)   // resists past wide open
    if (x > this.closedX) x = this.closedX     // nothing further to hide

    this.place(x)
    this.tracker.push(x, event.timeStamp)
  }

  pointerUp(event) {
    const drag = this.drag
    if (!drag || event.pointerId !== drag.id) return

    this.drag = null
    this.menuTarget.classList.remove("is-dragging")
    if (!drag.committed) return

    this.menuTarget.releasePointerCapture?.(drag.id)
    this.draggedAt = performance.now()

    // Where the throw was going, not where the finger stopped. A short flick
    // across a third of the sheet should close it; the same distance moved
    // slowly should not.
    const velocity = this.tracker.velocity
    const landing = this.x + project(velocity)

    landing > this.closedX / 2 ? this.close({ velocity }) : this.open({ velocity })
  }

  // ---- Motion ----------------------------------------------------------

  animateTo(x, velocity = 0) {
    this.animation?.stop()

    if (prefersReducedMotion()) {
      this.place(x)
      return this.settle()
    }

    this.menuTarget.style.willChange = "transform"
    this.animation = new Spring({
      from: this.x,
      to: x,
      velocity,
      // Apple ships 0.8 / 0.3 for a drawer: a little overshoot, because the
      // gesture that got it here carried momentum of its own.
      damping: 0.8,
      response: 0.3,
      onUpdate: (value) => this.place(value),
      onRest: () => this.settle()
    })
  }

  settle() {
    this.animation = null
    this.menuTarget.style.willChange = ""
    if (this.destination !== 0) this.hideAway()
  }

  // One position drives all of it: where the sheet is, how dark the page
  // behind it is, and how far the menu button has folded into a cross.
  place(x) {
    this.x = x

    const progress = Math.max(0, Math.min(1, 1 - x / this.width))

    this.menuTarget.style.transform = `translate3d(${x.toFixed(2)}px, 0, 0)`
    this.scrimTarget.style.opacity = progress.toFixed(3)
    this.toggleTarget.style.setProperty("--nav-p", progress.toFixed(3))
  }

  // ---- State -----------------------------------------------------------

  reveal() {
    this.menuTarget.classList.remove("hidden")
    this.scrimTarget.classList.remove("hidden")
    this.menuTarget.classList.add("is-open")
  }

  hideAway() {
    this.menuTarget.classList.add("hidden")
    this.scrimTarget.classList.add("hidden")
    this.menuTarget.classList.remove("is-open", "is-dragging")
  }

  setExpanded(open) {
    this.toggleTarget.setAttribute("aria-expanded", String(open))
    this.toggleTarget.querySelector(".sr-only").textContent = open ? "Luk menu" : "Åbn menu"
  }

  // Called on connect and whenever the breakpoint is crossed: above it the nav
  // is an ordinary row of pills and there is nothing to open or close.
  layout() {
    this.animation?.stop()
    this.animation = null
    this.drag = null
    this.unlockScroll()
    this.setExpanded(false)

    if (!this.desktop.matches) {
      this.destination = this.closedX
      this.place(this.closedX)
      return this.hideAway()
    }

    this.destination = 0
    this.menuTarget.classList.remove("hidden", "is-open", "is-dragging")
    this.menuTarget.style.transform = ""
    this.menuTarget.style.willChange = ""
    this.scrimTarget.classList.add("hidden")
    this.scrimTarget.style.opacity = ""
    this.toggleTarget.style.removeProperty("--nav-p")
  }

  // The button first, because it is the way out of the sheet and it sits above
  // it in reading order.
  get focusables() {
    const inside = Array.from(this.menuTarget.querySelectorAll("a[href], button:not([disabled])"))
    return [this.toggleTarget, ...inside]
  }

  get opened() {
    return this.destination === 0
  }

  get width() {
    return this.menuTarget.offsetWidth || 396
  }

  get closedX() {
    return this.width
  }

  // A drag that ended on the sheet or the scrim is followed by a click. That
  // click is not a second instruction.
  get recentlyDragged() {
    return performance.now() - this.draggedAt < 400
  }

  // ---- Page behind -----------------------------------------------------

  // The page must not scroll behind an open drawer. Where a scrollbar took up
  // room, give the room back, or the page jumps sideways as it is locked.
  lockScroll() {
    const gutter = window.innerWidth - document.documentElement.clientWidth

    document.body.style.overflow = "hidden"
    if (gutter > 0) document.body.style.paddingRight = `${gutter}px`
  }

  unlockScroll() {
    document.body.style.overflow = ""
    document.body.style.paddingRight = ""
  }
}
